/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Transition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';

import { Close } from '../../Icons';

import { ToastMessages } from '../../State';

import styles from './toast-message-wrapper.module.css';

interface ToastMessageProps {
    toastMessage: ToastMessages.Types.ToastMessage;
}

const duration = 300;
let timeout;

const defaultStyle = {
    transition: `all ${duration}ms ease-in-out`,
    right: '1rem',
};

const transitionStyles = {
    entering: { right: '-25rem' },
    entered: { right: '1rem' },
    exiting: { right: '1rem' },
    exited: { right: '-25rem' },
};

const ToastMessage: React.FC<ToastMessageProps> = ({ toastMessage }) => {
    const dispatch = useDispatch();

    const { id, title, titleI18n, message, messageI18n, action = undefined } = toastMessage;

    // Extract namespaces from all i18n keys
    // We need this to preload the JSON files for the i18n keys, that can be anything at this point in the application
    const i18nKeys = [titleI18n, messageI18n, action?.displayI18n].filter(Boolean);
    const namespaces = i18nKeys.map((key) => (key?.includes(':') ? key.split(':')[0] : undefined)).filter(Boolean);

    // Now preload whatever we identified
    const { t } = useTranslation(namespaces.length > 0 ? namespaces : []);

    // Helper function to get the display text (either translated or regular string)
    const getDisplayText = (regularText?: string, i18nKey?: string): string => {
        if (i18nKey) {
            return t(i18nKey, regularText || i18nKey); // Use regular text as fallback, or key if no regular text
        }
        return regularText || '';
    };

    const [componentIn, toggleComponentIn] = React.useState<boolean>(false);

    const closeMessage = () => {
        toggleComponentIn(false);

        dispatch({
            type: ToastMessages.Actions.REMOVE_MESSAGE,
            payload: id,
        } as ToastMessages.ActionTypes);

        clearTimeout(timeout);
    };

    const handleAction = (func) => {
        func();

        closeMessage();
    };

    React.useEffect(() => {
        toggleComponentIn(true);

        timeout = setTimeout(() => {
            closeMessage();
        }, 10000);
    }, []);

    return (
        <Transition in={componentIn} timeout={duration}>
            {(state) => (
                <div
                    className={styles.ToastMessage}
                    style={{
                        ...defaultStyle,
                        ...transitionStyles[state],
                    }}
                >
                    <div className={styles.ToastMessageIcon} onClick={closeMessage}>
                        <Close aspect="1rem" />
                    </div>
                    <h2>{getDisplayText(title, titleI18n)}</h2>
                    <p>{getDisplayText(message, messageI18n)}</p>
                    {action && (
                        <p
                            className={styles.Action}
                            onClick={() => {
                                handleAction(action.func);
                            }}
                        >
                            {getDisplayText(action.display, action.displayI18n)}
                        </p>
                    )}
                </div>
            )}
        </Transition>
    );
};

export default ToastMessage;
